/**
 * One item of plant, and everything that has happened to it.
 *
 * The register answers "what is wrong across the fleet". This answers "what
 * has this machine been through", which is a different question asked by a
 * different person — usually somebody deciding whether to repair it again or
 * replace it, and usually while holding the thing in front of them.
 *
 * Four sources, in one order, newest first, and each says which it is:
 *
 *   the work raised against it   — the platform's work orders, by asset tag
 *   what is wrong with it now    — its open defects
 *   what people wrote about it   — notes, append-only and attributed
 *   what was done to it          — the maintenance log on the register
 *
 * They are NOT merged into one undifferentiated stream. A work order, a defect
 * and somebody's note are different kinds of record with different weight, and
 * a timeline that flattens them invites a reader to treat a handover remark as
 * evidence. They are shown together, in one column, each labelled.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import {
  CONDITION_LABEL,
  FLEET_ASSET_TYPES,
  fleetFromAssets,
  isDown,
  KIND_LABEL,
  rowFor,
  type FleetItem,
} from "../../domain/fleet";
import { NOTE_CATEGORIES, NOTE_CATEGORY_LABEL, type NoteCategory } from "../../domain/estate";
import { Button, Card, EmptyState, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { x as ex } from "../executive/shared";
import { EQUIPMENT_SPEC } from "../../domain/registerSpecs";
import { RegisterNotices } from "../shared/RegisterNotices";
import { useRegisterDoc } from "../shared/useRegisterRows";
import { ChangeLog, ItemActions } from "./FleetEditors";
import { historyOf } from "../../domain/fleetEdit";
import { t, tn } from "../../i18n";
import s from "./Equipment.module.css";

/** Notes about one item live under their own topic, so a read is one call. */
export const noteTopicFor = (tag: string) => `equipment.${tag}`;

export function FleetHistoryPage() {
  const session = useSession();
  const qc = useQueryClient();
  const { tag = "" } = useParams<{ tag: string }>();
  const entered = useRegisterDoc(EQUIPMENT_SPEC);

  const platform = useQuery({
    queryKey: ["fleet-assets", session.site.id],
    staleTime: 60_000,
    queryFn: () => session.source.listAssetsOfTypes(session.site.id, FLEET_ASSET_TYPES),
  });
  const estate = useQuery({
    queryKey: ["fleet-defects", session.site.id],
    staleTime: 60_000,
    queryFn: () => session.source.getEstateSummary(session.site.id, [...FLEET_ASSET_TYPES]),
  });
  /*
   * Every work order at the site, filtered here by the asset tag.
   *
   * `WorkOrderQuery` has no asset filter and the platform's own endpoint does
   * not take one, so this is the same client-side filtering the query's own
   * comment describes for status. It is honest but it is not free: on a large
   * estate the page the platform returns may not contain this asset's older
   * work at all, and `truncated` below says when that is the case rather than
   * showing a short list as a complete one.
   */
  const orders = useQuery({
    queryKey: ["fleet-history-wo", session.site.id],
    staleTime: 30_000,
    queryFn: () => session.source.listWorkOrders({ siteId: session.site.id }),
  });
  const mine = (orders.data?.items ?? []).filter((w) => w.assetTag === tag);
  const notes = useQuery({
    queryKey: ["fleet-history-notes", session.site.id, tag],
    staleTime: 30_000,
    queryFn: () => session.source.listNotes(session.site.id, noteTopicFor(tag)),
  });

  const fromPlatform = (platform.data?.length ?? 0) > 0;
  const item: FleetItem | undefined = useMemo(() => {
    if (fromPlatform) {
      const byTag = new Map<string, { summary: string; severity: number; typeKey?: string }[]>();
      for (const d of estate.data?.defects.openList ?? []) {
        const list = byTag.get(d.assetTag) ?? [];
        list.push({ summary: d.summary, severity: d.severity, typeKey: d.defectType });
        byTag.set(d.assetTag, list);
      }
      return fleetFromAssets(
        (platform.data ?? []).map((a) => ({ ...a, openDefects: byTag.get(a.tag) ?? [] })),
      ).items.find((x) => x.tag === tag);
    }
    return entered.value.items.find((x) => x.tag === tag);
  }, [fromPlatform, platform.data, estate.data, entered.value, tag]);
  const changes = item && !fromPlatform ? historyOf(entered.value, item.id) : [];

  const openDefects = (estate.data?.defects.openList ?? []).filter((d) => d.assetTag === tag);
  const row = item ? rowFor(item, new Date()) : null;

  const [body, setBody] = useState("");
  const [category, setCategory] = useState<NoteCategory>("observation");
  const [saving, setSaving] = useState(false);
  const addNote = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await session.source.addNote(session.site.id, {
        topic: noteTopicFor(tag),
        category,
        body: body.trim(),
        assetTag: tag,
      });
      setBody("");
      await qc.invalidateQueries({ queryKey: ["fleet-history-notes", session.site.id, tag] });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page
      eyebrow={t("{site} · {suite} · {screen}", {
        site: session.site.code,
        suite: t("Asset Management"),
        screen: t("Equipment"),
      })}
      title={item ? `${item.tag} — ${item.name}` : tag}
      actions={
        <div className={s.editBar}>
          {item && !fromPlatform && (
            <ItemActions item={item} register={entered.value} onSave={(next, base) => entered.commit(next, base)} />
          )}
          <Link className={ex.tag} to="/equipment" data-testid="fleet-back">
            {t("Back to the register")}
          </Link>
        </div>
      }
    >
      {item && !fromPlatform && <RegisterNotices doc={entered} what={t("equipment register")} />}
      {(platform.isPending || orders.isPending) && <Skeleton rows={6} />}
      {orders.isError && <ErrorState error={orders.error} retry={() => void orders.refetch()} />}

      {!item && !platform.isPending && !entered.loading && (
        <EmptyState>{t("Nothing on the register is tagged {tag}.", { tag })}</EmptyState>
      )}

      {item && row && (
        <div className={ex.page}>
          {item.retired && (
            <div className={s.retiredBanner} data-testid="history-retired">
              <b>{t("Retired")}</b>{" "}
              {t("on {date} by {who}: {why}", {
                date: item.retired.at.slice(0, 10),
                who: item.retired.by,
                why: item.retired.reason,
              })}
            </div>
          )}
          <Card
            title="What it is"
            sub={t("{owner} · {kind}", { owner: item.owner.name, kind: t(KIND_LABEL[item.kind]) })}
          >
            <dl className={ex.kv}>
              <div>
                <dt>{t("Condition")}</dt>
                <dd>
                  <span className={isDown(item.condition) ? ex.tagRed : ex.tagGreen}>
                    {t(CONDITION_LABEL[item.condition])}
                  </span>
                  {item.conditionNote && <div className={ex.muted}>{item.conditionNote}</div>}
                </dd>
              </div>
              <div>
                <dt>{t("Department")}</dt>
                <dd>
                  {item.department ?? t("not recorded")}
                  {item.usedByDepartment && (
                    <div className={ex.muted}>{t("used by {dept}", { dept: item.usedByDepartment })}</div>
                  )}
                </dd>
              </div>
              <div>
                <dt>{t("Driver")}</dt>
                <dd>{item.driver?.name ?? t("none assigned")}</dd>
              </div>
              <div>
                <dt>{t("Location")}</dt>
                <dd>{item.location ?? t("not recorded")}</dd>
              </div>
              <div>
                <dt>{t("Registration")}</dt>
                <dd>{item.registration ?? t("not recorded")}</dd>
              </div>
              <div>
                <dt>{t("Hours run")}</dt>
                <dd>{item.hoursRun != null ? item.hoursRun.toLocaleString() : t("not recorded")}</dd>
              </div>
            </dl>
            {row.flags.length > 0 && (
              <>
                <div className={ex.colTitle}>{t("What stops it today")}</div>
                <ul className={ex.plain} data-testid="history-flags">
                  {row.flags.map((f) => (
                    <li key={f.what}>
                      <span className={f.kind === "expiring" ? ex.tag : ex.tagRed}>{t(f.kind)}</span> {t(f.what)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>

          {!fromPlatform && (
            <Card
              title="Changes to the record"
              sub="Who changed what, when, before and after."
            >
              <ChangeLog changes={changes} />
            </Card>
          )}

          <Card
            title="Work raised against it"
            sub="Platform work orders for this tag"
          >
            {mine.length === 0 ? (
              <EmptyState>{t("No work order has been raised against {tag}.", { tag })}</EmptyState>
            ) : (
              <div className={ex.tableWrap}>
                <table className={ex.table} data-testid="history-work-orders">
                  <tbody>
                    {mine.map((w) => (
                      <tr key={w.id}>
                        <td>{w.openedAt?.slice(0, 10)}</td>
                        <td>
                          <Link to={`/work-orders/${w.id}`}>
                            <b>{w.number}</b>
                          </Link>
                          <div className={ex.muted}>{w.title}</div>
                        </td>
                        <td>
                          <span className={ex.tag}>{t(w.status?.replace("_", " ") ?? "")}</span>
                          {w.crew && <div className={ex.muted}>{w.crew}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {orders.data?.truncated && (
              <p className={ex.muted}>
                {t(
                  "The platform returned the first page of the site's work orders and said it was truncated, so older work against this item may not be in the list above.",
                )}
              </p>
            )}
          </Card>

          <Card title="What is wrong with it now" sub="its open defects on the platform">
            {openDefects.length === 0 ? (
              <EmptyState>{t("No open defect against {tag}.", { tag })}</EmptyState>
            ) : (
              <ul className={ex.plain} data-testid="history-defects">
                {openDefects.map((d) => (
                  <li key={d.id}>
                    <span className={d.severity >= 4 ? ex.tagRed : ex.tag}>{t("severity {n}", { n: d.severity })}</span>{" "}
                    <b>{d.summary}</b>
                    <div className={ex.muted}>
                      {[d.defectTypeName ?? d.defectType, d.detectedAt?.slice(0, 10)].filter(Boolean).join(" · ")}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="Notes"
            sub="Notes can't be edited or deleted."
          >
            <div className={ex.row}>
              <Field label={t("Kind")}>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as NoteCategory)}
                  aria-label={t("Kind")}
                >
                  {NOTE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(NOTE_CATEGORY_LABEL[c])}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("Note")}>
                <Input
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t("What happened, what you did, what's next")}
                  aria-label={t("Note")}
                />
              </Field>
              <Button variant="primary" onClick={() => void addNote()} disabled={saving || !body.trim()}>
                {saving ? t("Saving…") : t("Add the note")}
              </Button>
            </div>
            {(notes.data?.length ?? 0) === 0 ? (
              <EmptyState>{t("Nothing has been written about {tag} yet.", { tag })}</EmptyState>
            ) : (
              <div className={s.notes} data-testid="history-notes">
                {notes.data?.map((n) => (
                  <div key={n.id} className={s.noteRow}>
                    <div className={s.noteHead}>
                      <span className={s.noteCat}>{t(NOTE_CATEGORY_LABEL[n.category])}</span>
                      <span className={s.noteMeta}>
                        {[n.createdAt?.slice(0, 16).replace("T", " "), n.createdBy].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    <div className={s.noteText}>{n.body}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="What was done to it" sub="the maintenance log carried on the register">
            {(item.maintenance ?? []).length === 0 ? (
              <EmptyState>
                {t(
                  "Nothing recorded. On a platform-read register the log lives in the work orders above; the entered register carries its own.",
                )}
              </EmptyState>
            ) : (
              <div className={ex.tableWrap}>
                <table className={ex.table} data-testid="history-log">
                  <tbody>
                    {[...(item.maintenance ?? [])]
                      .sort((a, b) => b.at.localeCompare(a.at))
                      .map((m) => (
                        <tr key={`${m.at}-${m.what}`}>
                          <td>{m.at}</td>
                          <td>
                            <b>{m.what}</b>
                            <div className={ex.muted}>{[t(m.kind), m.by, m.workOrder].filter(Boolean).join(" · ")}</div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className={ex.muted}>
              {tn(row.maintenanceCount, "{n} entry on the register", "{n} entries on the register")}
              {row.lastMaintenanceAt ? t(", last {date}", { date: row.lastMaintenanceAt }) : ""}
            </p>
          </Card>
        </div>
      )}
    </Page>
  );
}
